import { useState } from 'react'
import { Plus, Minus } from 'lucide-react'
import { Link } from 'react-router-dom'
import SectionHead from './SectionHead'

const FAQS: { q: string; a: React.ReactNode }[] = [
  {
    q: 'Do I need a technical background?',
    a: 'No. Day One is built for marketing, operations, HR and finance teams. The agents handle the technical parts. You bring what you know about your own work.',
  },
  {
    q: 'How long does a session take?',
    a: 'A solo session takes about 25 minutes. Most teams finish all five stages in about 90. There is no clock, so you can go slower.',
  },
  {
    q: 'What will our IT team need to know?',
    a: (
      <>
        Day One runs in the browser. Nothing to install, no extension, no integration to
        configure. Some corporate web filters do block domains they have not seen before,
        so if the page will not load, send whoever owns filtering our{' '}
        <Link to="/security" className="text-teal underline underline-offset-2 font-semibold">
          IT and security guide
        </Link>
        . It lists every domain to allow, our subprocessors, and how long we keep data.
      </>
    ),
  },
  {
    q: 'What happens to our data?',
    a: (
      <>
        Session data is stored on Google Cloud, encrypted at rest and in transit. We do not
        sell it and it is never used to train AI models. You can download everything during
        or after a session, and delete your account and its data yourself from the account
        menu. The{' '}
        <Link to="/dpa" className="text-teal underline underline-offset-2 font-semibold">
          data processing agreement
        </Link>{' '}
        is published, with the full subprocessor list.
      </>
    ),
  },
  {
    q: 'Solo session or team session?',
    a: 'A solo session is you and the three agents on a workflow you own, about 25 minutes. A team session brings up to 12 people into one room for about 90, and what comes out is documentation the whole team agreed on. Both cover all five stages. Most people run solo first, then bring the team.',
  },
  {
    q: 'How much does it cost?',
    a: 'Nothing. There is no card to enter, no trial clock, and no part of the workshop held back. You do sign in first, with Google or an email you can verify, so the session saves to your account and the results reach your inbox.',
  },
  {
    q: 'What do we walk away with?',
    a: 'A tested prompt built from your real documentation, a session summary, the documented process itself, and a method you can run again on another workflow.',
  },
  {
    q: 'Why three different agents?',
    a: 'Each has one job: Scout guides the session, Archivist picks apart documentation, Builder writes and tests the prompt. Splitting the work is how the better AI systems are put together, and watching the handoffs is part of the point.',
  },
  {
    q: 'What if it does not work for our team?',
    a: 'Every team is different. If your session produces nothing useful, write to us and we will work through it with you. The method itself is yours to keep and repeat.',
  },
]

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="bg-surface py-14 md:py-16 border-y-2 border-navy">
      <div className="mktg-container">
        <SectionHead mark="SECT. 08" title="Questions" />

        <div className="ledger max-w-3xl">
          {FAQS.map((faq, i) => {
            const open = openIndex === i
            return (
              <div key={faq.q}>
                <button
                  onClick={() => setOpenIndex(open ? null : i)}
                  aria-expanded={open}
                  className="w-full flex items-baseline gap-4 px-5 py-4 text-left cursor-pointer hover:bg-paper transition-colors"
                >
                  <span className="mono-label text-teal tabular-nums shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="font-display text-base md:text-lg text-navy flex-1">
                    {faq.q}
                  </span>
                  {open ? (
                    <Minus size={16} className="text-navy shrink-0 self-center" />
                  ) : (
                    <Plus size={16} className="text-navy/70 shrink-0 self-center" />
                  )}
                </button>
                <div className={`mktg-faq-answer ${open ? 'open' : ''}`}>
                  <div>
                    <p className="pl-5 pr-5 sm:pl-[4.6rem] pb-5 text-navy/75 text-sm md:text-base leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
